"use client"

import React, { useEffect, useState } from "react"
import { X } from "lucide-react"

const ResponsiveModalWrapper = ({
  show,
  onClose,
  title,
  icon,
  children,
  maxWidth = "max-w-2xl",
  maxHeight = "max-h-[90vh]",
  showCloseButton = true,
  headerActions = null,
  footerContent = null,
  closeOnEscape = true,
  closeOnOverlay = true,
  customHeaderBg = null,
  customCloseColor = null,
  fullScreenOnMobile = false,
}) => {
  const [isAnimating, setIsAnimating] = useState(false)

  // Manejo de tecla Escape y scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && closeOnEscape && show) {
        onClose()
      }
    }

    if (show) {
      setIsAnimating(true)
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"
    } else {
      setIsAnimating(false)
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = "unset"
    }
  }, [show, onClose, closeOnEscape])

  if (!show) return null

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlay) {
      onClose()
    }
  }

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-50 p-2 sm:p-4 transition-all duration-300 ease-out ${isAnimating ? "opacity-100" : "opacity-0"}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      style={{ backgroundColor: "rgba(45, 45, 45, 0.7)" }}
    >
      <div
        className={`
          rounded-xl sm:rounded-2xl 
          w-full ${maxWidth} ${maxHeight}
          ${fullScreenOnMobile ? "h-full sm:h-auto" : "max-h-[95vh]"}
          mx-2 sm:mx-4 
          overflow-hidden 
          transform transition-all duration-300 ease-out
          border shadow-2xl
          ${isAnimating ? "translate-y-0 opacity-100 scale-100" : "translate-y-8 opacity-0 scale-95"}
        `}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "white",
          borderColor: "#C8A88C",
        }}
      >
        {/* Header del modal con colores café elegantes */}
        {(title || icon || showCloseButton || headerActions) && (
          <div
            className="flex items-center justify-between p-4 sm:p-6 border-b"
            style={{
              backgroundColor: customHeaderBg || "#C8A88C",
              borderBottomColor: "#A1887F",
            }}
          >
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              {icon && (
                <div className="flex-shrink-0 p-2 rounded-lg" style={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}>
                  {React.cloneElement(icon, {
                    className: "h-5 w-5 sm:h-6 sm:w-6 text-white",
                  })}
                </div>
              )}
              {title && (
                <h3 id="modal-title" className="text-lg sm:text-xl font-bold text-white truncate">
                  {title}
                </h3>
              )}
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              {/* Acciones adicionales del header */}
              {headerActions && <div className="flex items-center space-x-2 text-white">{headerActions}</div>}

              {/* 🆕 BOTÓN DE CERRAR SIMPLE BLANCO SOBRE FONDO CAFÉ */}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="
                    p-2 sm:p-3 
                    text-white hover:text-gray-200
                    transition-all duration-200
                    rounded-lg hover:bg-white/10
                    touch-manipulation
                    min-w-[44px] min-h-[44px]
                    flex items-center justify-center
                    hover:scale-105 active:scale-95
                  "
                  aria-label="Cerrar modal"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Contenido del modal con fondo blanco limpio */}
        <div
          className={`
            overflow-y-auto bg-white
            ${fullScreenOnMobile ? "h-full sm:max-h-[70vh]" : "max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-120px)]"}
            overscroll-contain
          `}
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#C8A88C #F5F5F5",
          }}
        >
          {children}
        </div>

        {/* Footer del modal (opcional) */}
        {footerContent && (
          <div
            className="border-t p-4 sm:p-6"
            style={{
              borderTopColor: "#E1E0E0",
              backgroundColor: "#F9F9F9",
            }}
          >
            {footerContent}
          </div>
        )}
      </div>

      {/* Estilos CSS mejorados */}
      <style jsx>{`
        /* Scroll personalizado para Webkit */
        .overflow-y-auto::-webkit-scrollbar {
          width: 8px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #F5F5F5;
          border-radius: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #C8A88C;
          border-radius: 4px;
          border: 1px solid #F5F5F5;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #A1887F;
        }
        
        /* Estados focus mejorados para accesibilidad */
        button:focus-visible {
          outline: 2px solid #C8A88C;
          outline-offset: 2px;
        }
        
        /* Mejoras responsive específicas */
        @media (max-width: 640px) {
          button {
            min-height: 44px;
            min-width: 44px;
          }
        }
        
        /* Animaciones reducidas para usuarios que prefieren menos movimiento */
        @media (prefers-reduced-motion: reduce) {
          .transition-all {
            transition: none;
          }
          
          .transform {
            transform: none;
          }
        }
      `}</style>
    </div>
  )
}

export default ResponsiveModalWrapper